/**
 * Wallet Service for Thaklis Pro.
 * TC bank accounts, customer wallets, wallet transactions and top-up requests.
 */

import ApiService from './api.service';

// snake_case <-> camelCase conversion.
const snakeToCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
const camelToSnake = (s) => s.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());

const keysSnakeToCamel = (obj) => {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(keysSnakeToCamel);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[snakeToCamel(k)] = v;
  }
  return out;
};

const keysCamelToSnake = (obj) => {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[camelToSnake(k)] = v;
  }
  return out;
};

// Map a backend TopUpRequestResponse to the shape the UI already expects.
// Backend DTO uses `amount`; UI treats it as `requestedAmount` and (on APPROVED) also as `approvedAmount`.
const mapTopupRow = (row) => {
  if (!row) return row;
  const camel = keysSnakeToCamel(row);
  return {
    ...camel,
    requestedAmount: camel.amount,
    approvedAmount: camel.status === 'APPROVED' ? camel.amount : null,
    aiValidation: camel.aiValidationResult || null
  };
};

// ============================================================
// SERVICE
// ============================================================

const WalletService = {
  // ----- Customer Wallets (real backend) -----
  getCustomerWallets: async (params = {}) => {
    const response = await ApiService.getCustomerWallets(params.typingCenterId);
    const list = (response?.data?.data || []).map(keysSnakeToCamel);
    const filtered = params.search
      ? list.filter(w => (w.organizationName || '').toLowerCase().includes(params.search.toLowerCase()))
      : list;
    return { data: filtered, total: filtered.length };
  },

  getCustomerWallet: async (walletId) => {
    const response = await ApiService.getCustomerWallet(walletId);
    const row = response?.data?.data;
    return row ? keysSnakeToCamel(row) : null;
  },

  getCustomerWalletByOrg: async (organizationId) => {
    const response = await ApiService.getCustomerWalletByOrg(organizationId);
    const row = response?.data?.data;
    return row ? keysSnakeToCamel(row) : null;
  },

  getPortalWallet: async () => {
    const response = await ApiService.getPortalWallet();
    const row = response?.data?.data;
    return row ? keysSnakeToCamel(row) : null;
  },

  // Removed clients that still hold a wallet balance to refund to the customer
  getWalletsPendingRefund: async (typingCenterId) => {
    const response = await ApiService.getWalletsPendingRefund(typingCenterId);
    return (response?.data?.data || []).map(keysSnakeToCamel);
  },

  // Return a client's wallet balance to the customer, paid from a TC bank. Omit `amount` for the full balance.
  refundCustomerWallet: async ({ organizationId, bankAccountId, amount }) => {
    const payload = { organizationId, bankAccountId };
    if (amount !== undefined && amount !== null && amount !== '') payload.amount = amount;
    const response = await ApiService.refundCustomerWallet(payload);
    return response?.data;
  },

  getWalletSummary: async (typingCenterId) => {
    const response = await ApiService.getCustomerWalletSummary(typingCenterId);
    const row = response?.data?.data;
    return row ? keysSnakeToCamel(row) : {
      totalApprovedBalance: 0,
      totalUnapprovedBalance: 0,
      totalCustomers: 0,
      pendingTopups: 0
    };
  },

  // ----- Wallet Transactions (real backend) -----
  // `total` is the full server-side match count, not the page length.
  getWalletTransactions: async (walletId, params = {}) => {
    const response = await ApiService.getCustomerWalletTransactions(walletId, params);
    const pageData = response?.data?.data || {};
    const rows = Array.isArray(pageData.content) ? pageData.content : [];
    return {
      data: rows.map(keysSnakeToCamel),
      total: pageData.total_elements ?? pageData.totalElements ?? 0
    };
  },

  getWalletTransactionSummary: async (walletId) => {
    const response = await ApiService.getCustomerWalletTransactionsSummary(walletId);
    const row = response?.data?.data;
    return row ? keysSnakeToCamel(row) : { totalCredits: 0, totalDebits: 0, transactionCount: 0 };
  },

  // Portal roles cannot reach /api/customer-wallets; no portal ledger endpoint exists yet.
  getAllTransactions: async () => {
    return { data: [], total: 0 };
  },

  // ----- TC Bank Accounts (real backend) -----
  // Own-account transfer; returns the journal entry. Fees: { type: FLAT|PERCENT, value } or null.
  transfer: async (data) => {
    const response = await ApiService.createTcBankTransfer(keysCamelToSnake(data));
    return keysSnakeToCamel(response?.data?.data);
  },

  reverseTransfer: async (transferId, reason) => {
    const response = await ApiService.reverseTcBankTransfer(transferId, reason);
    return keysSnakeToCamel(response?.data?.data);
  },

  getBankAccounts: async () => {
    const response = await ApiService.getTcBankAccounts();
    const list = response?.data?.data || [];
    return list.map(keysSnakeToCamel);
  },

  getPortalBankAccounts: async () => {
    const response = await ApiService.getPortalWalletBankAccounts();
    const list = response?.data?.data || [];
    return list.map(keysSnakeToCamel);
  },

  getBankAccount: async (id) => {
    const response = await ApiService.getTcBankAccount(id);
    const row = response?.data?.data;
    return row ? keysSnakeToCamel(row) : null;
  },

  createBankAccount: async (data) => {
    const response = await ApiService.createTcBankAccount(keysCamelToSnake(data));
    return keysSnakeToCamel(response?.data?.data);
  },

  updateBankAccount: async (id, data) => {
    const { typingCenterId, isDefault, isActive, id: _omit, balance, createdAt, updatedAt, ...editable } = data;
    const response = await ApiService.updateTcBankAccount(id, keysCamelToSnake(editable));
    return keysSnakeToCamel(response?.data?.data);
  },

  deleteBankAccount: async (id) => {
    await ApiService.deleteTcBankAccount(id);
    return { success: true };
  },

  setDefaultBank: async (id) => {
    const response = await ApiService.setDefaultTcBankAccount(id);
    return keysSnakeToCamel(response?.data?.data);
  },

  setBankAccountActive: async (id, active) => {
    const response = await ApiService.setTcBankAccountActive(id, active);
    return keysSnakeToCamel(response?.data?.data);
  },

  // ----- Top-Up Requests (real backend) -----
  /**
   * List top-up requests. If {@code organizationId} is passed the portal
   * endpoint is used (portal JWT scopes to the caller's org, the param
   * is ignored). Otherwise the managers endpoint is used, optionally
   * filtered by typing center.
   */
  getTopupRequests: async (params = {}) => {
    const response = params.organizationId
      ? await ApiService.getPortalTopUpRequests()
      : await ApiService.getTopUpRequests(params.typingCenterId);
    let list = (response?.data?.data || []).map(mapTopupRow);
    if (params.status) list = list.filter(r => r.status === params.status);
    if (params.search) {
      const s = params.search.toLowerCase();
      list = list.filter(r => (r.organizationName || '').toLowerCase().includes(s));
    }
    return { data: list, total: list.length };
  },

  getTopupRequest: async (id) => {
    const response = await ApiService.getTopUpRequestById(id);
    return mapTopupRow(response?.data?.data);
  },

  approveTopup: async (id, bankReference) => {
    const response = await ApiService.approveTopUpRequest(id, bankReference);
    return mapTopupRow(response?.data?.data);
  },

  rejectTopup: async (id, reason) => {
    const response = await ApiService.rejectTopUpRequest(id, reason);
    return mapTopupRow(response?.data?.data);
  },

  submitTopupForWallet: async (walletId, { tcBankAccountId, amount, remarks, bankReference }, file) => {
    const response = await ApiService.submitWalletTopUp(walletId, { tcBankAccountId, amount, remarks, bankReference }, file);
    return mapTopupRow(response?.data?.data);
  },

  submitPortalTopup: async ({ tcBankAccountId, amount, remarks, bankReference }, file) => {
    const response = await ApiService.submitPortalTopUp({ tcBankAccountId, amount, remarks, bankReference }, file);
    return mapTopupRow(response?.data?.data);
  },

  cancelPortalTopup: async (id) => {
    const response = await ApiService.cancelPortalTopUp(id);
    return mapTopupRow(response?.data?.data);
  },

  // ----- Helpers -----
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(amount || 0);
  },

  formatCurrencyOrDash: (amount) => {
    if (amount === null || amount === undefined || amount === '') return '—';
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(amount);
  },

  getTransactionTypeLabel: (type) => ({
    TOPUP: 'Top-Up',
    DIRECT_TOPUP: 'Direct Top-Up',
    INVOICE_PAYMENT: 'Invoice Payment',
    SETTLEMENT_DEDUCTION: 'Settlement',
    REFUND: 'Refund',
    CUSTOMER_REFUND: 'Refund to Customer',
    TAX_PAYMENT: 'Tax Payment',
    THAKLIS_PAYMENT: 'Thaklis Payment',
    PAYOUT: 'Payout',
    ADJUSTMENT: 'Adjustment',
    CHEQUE_TOPUP: 'Cheque Top-Up',
    CREDIT_NOTE: 'Credit Note'
  }[type] || type),

  getTransactionTypeColor: (type) => ({
    TOPUP: 'success',
    DIRECT_TOPUP: 'info',
    INVOICE_PAYMENT: 'warning',
    SETTLEMENT_DEDUCTION: 'warning',
    REFUND: 'secondary',
    CUSTOMER_REFUND: 'secondary',
    TAX_PAYMENT: 'warning',
    THAKLIS_PAYMENT: 'warning',
    PAYOUT: 'warning',
    ADJUSTMENT: 'default',
    CHEQUE_TOPUP: 'success',
    CREDIT_NOTE: 'info'
  }[type] || 'default'),

  getTransactionStatusLabel: (status) => ({
    PENDING: 'Pending',
    COMPLETED: 'Completed',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled'
  }[status] || status),

  getTransactionStatusColor: (status) => ({
    PENDING: 'warning',
    COMPLETED: 'success',
    REJECTED: 'error',
    CANCELLED: 'default'
  }[status] || 'default'),

  getTopupStatusLabel: (status) => ({
    PENDING: 'Pending Review',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled'
  }[status] || status),

  getTopupStatusColor: (status) => ({
    PENDING: 'warning',
    APPROVED: 'success',
    REJECTED: 'error',
    CANCELLED: 'default'
  }[status] || 'default')
};

export default WalletService;
